import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Avatar,
  Rating,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, FormatQuote } from '@mui/icons-material';
import { SectionTitle } from '../common';
import { fadeInUp, viewportConfig } from '../../utils/animations';
import { TESTIMONIALS } from '../../utils/constants';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? TESTIMONIALS.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === TESTIMONIALS.length - 1 ? 0 : prev + 1));
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: 'easeIn',
      },
    }),
  };

  const currentTestimonial = TESTIMONIALS[currentIndex];

  return (
    <Box
      id="testimonials"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201, 169, 97, 0.1) 0%, transparent 70%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionTitle
          overline="Testimonials"
          title="What Our Clients Say"
          subtitle="Don't just take our word for it. Here's what property owners have to say about their experience with ITorama."
          light
        />

        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          sx={{
            maxWidth: 800,
            mx: 'auto',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <FormatQuote
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: 'secondary.main',
              opacity: 0.3,
              position: 'absolute',
              top: { xs: -20, md: -30 },
              left: { xs: 0, md: -40 },
              transform: 'rotate(180deg)',
            }}
          />

          <Box
            sx={{
              minHeight: { xs: 280, md: 250 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              px: { xs: 2, md: 6 },
            }}
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <Box
                key={currentIndex}
                component={motion.div}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                sx={{
                  position: 'absolute',
                  width: '100%',
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Avatar
                    src={currentTestimonial.avatar}
                    alt={currentTestimonial.name}
                    sx={{
                      width: { xs: 70, md: 80 },
                      height: { xs: 70, md: 80 },
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid',
                      borderColor: 'secondary.main',
                      fontSize: '1.5rem',
                      backgroundColor: 'secondary.main',
                      color: 'primary.main',
                    }}
                  >
                    {currentTestimonial.name.charAt(0)}
                  </Avatar>
                  <Rating
                    value={currentTestimonial.rating}
                    readOnly
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: 'secondary.main',
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  component="blockquote"
                  sx={{
                    color: 'white',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    lineHeight: 1.8,
                    mb: 3,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
                  "{currentTestimonial.quote}"
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {currentTestimonial.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  {currentTestimonial.role}
                </Typography>
              </Box>
            </AnimatePresence>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mt: 4,
            }}
          >
            <IconButton
              onClick={handlePrev}
              aria-label="Previous testimonial"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'primary.main',
                },
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {TESTIMONIALS.map((_, index) => (
                <Box
                  key={index}
                  component="button"
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  aria-label={`Go to testimonial ${index + 1}`}
                  sx={{
                    width: index === currentIndex ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: index === currentIndex ? 'secondary.main' : 'rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: index === currentIndex ? 'secondary.main' : 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={handleNext}
              aria-label="Next testimonial"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'primary.main',
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;
